import React, { useState, useEffect, useRef } from 'react';
import { TypeItem, TypeElement, ElementValueType } from '@/lib/stores/typesStore';
import { Button } from   '@/components/ui/button';
import { Input } from   '@/components/ui/InputDefault';
import { Textarea } from   '@/components/ui/textarea';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UnifiedWizardHeader } from   '@/components/wizards/shared/UnifiedWizardHeader';
import { VariableUsagePanel } from   '@/components/variables';
import { InlineElementRow } from './InlineElementRow';
import { LinkedValueSelector } from './LinkedValueSelector';
import { VariableValueType, VALUE_TYPE_OPTIONS } from '../types';

interface InlineElementEditorProps {
  variable: TypeItem;
  allVariables: TypeItem[];
  gameId: string;
  onElementCreate: (element: Omit<TypeElement, 'id'>) => void;
  onElementUpdate: (element: TypeElement) => void;
  onElementDelete: (elementId: string) => void;
  onVariableUpdate: (updates: Partial<TypeItem>) => void;
  onVariableDelete: () => void;
  onVariableSave?: (finalName?: string) => Promise<void>; // For new variables - saves to server
  initialEditMode?: boolean;
  isNewVariable?: boolean;
}

export const InlineElementEditor: React.FC<InlineElementEditorProps> = ({
  variable,
  allVariables,
  gameId,
  onElementCreate,
  onElementUpdate,
  onElementDelete,
  onVariableUpdate,
  onVariableDelete,
  onVariableSave,
  initialEditMode = false,
  isNewVariable = false,
}) => {
  const getValueType = (elementType: string): VariableValueType => {
    switch (elementType) {
      case 'number': return 'number';
      case 'percentage': return 'percentage';
      case 'range': return 'range';
      case 'type': return 'type';
      case 'boolean': return 'boolean';
      default: return 'string';
    }
  };

  const valueType = getValueType(variable.element_type);

  // Mode state for view/edit - start in edit mode if initialEditMode is true
  const [mode, setMode] = useState<'view' | 'edit'>(initialEditMode ? 'edit' : 'view');
  
  // Name editing
  const [editedName, setEditedName] = useState(variable.name);

  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newNumericValue, setNewNumericValue] = useState<number | undefined>();
  const [newBooleanValue, setNewBooleanValue] = useState<boolean>(false);
  const [newRangeMin, setNewRangeMin] = useState<number | undefined>();
  const [newRangeMax, setNewRangeMax] = useState<number | undefined>();
  const [newIncrement, setNewIncrement] = useState<number | undefined>();
  const [newLinkedVariableId, setNewLinkedVariableId] = useState<string | undefined>();
  const [newLinkedElementId, setNewLinkedElementId] = useState<string | undefined>();
  const newValueInputRef = useRef<HTMLInputElement>(null);

  const [variableDescription, setVariableDescription] = useState(variable.description || "");
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const descriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setVariableDescription(variable.description || "");
    setEditedName(variable.name);
  }, [variable.description, variable.name]);

  // Header handlers
  const handleEdit = () => {
    setMode('edit');
  };

  const handleCancel = () => {
    if (isNewVariable) {
      // For new variables, cancel means discard and go back
      onVariableDelete();
    } else {
      setEditedName(variable.name);
      setMode('view');
    }
  };

  const handleSave = async () => {
    const finalName = editedName.trim();
    
    // Update name if changed
    if (finalName !== variable.name) {
      onVariableUpdate({ name: finalName });
    }
    
    if (isNewVariable && onVariableSave) {
      // For new variables, pass the final name to ensure it's saved correctly
      await onVariableSave(finalName);
    } else {
      // For existing variables, just switch to view mode
      setMode('view');
    }
  };

  // Name change handler (in edit mode)
  const handleTitleChange = (newTitle: string) => {
    setEditedName(newTitle);
  };

  const handleDescriptionChange = (value: string) => {
    if (mode === 'view') return;
    
    setVariableDescription(value);
    setDescriptionDirty(true);

    if (descriptionTimeoutRef.current) {
      clearTimeout(descriptionTimeoutRef.current);
    }

    descriptionTimeoutRef.current = setTimeout(() => {
      onVariableUpdate({ description: value });
      setDescriptionDirty(false);
    }, 500);
  };

  const handleDescriptionBlur = () => {
    if (mode === 'view') return;
    
    if (descriptionDirty) {
      if (descriptionTimeoutRef.current) {
        clearTimeout(descriptionTimeoutRef.current);
      }
      onVariableUpdate({ description: variableDescription });
      setDescriptionDirty(false);
    }
  };

  // Add element and optionally keep the row open for continuous entry
  const handleAddElement = (keepOpen: boolean = false) => {
    if (!newValue.trim()) return;

    let elementValueType: ElementValueType = 'none';
    if (valueType === 'number' || valueType === 'percentage') {
      elementValueType = 'single';
    } else if (valueType === 'range') {
      elementValueType = 'range';
    } else if (valueType === 'boolean') {
      elementValueType = 'single';
    }

    onElementCreate({
      name: newValue.trim(),
      description: newDescription.trim() || undefined,
      valueType: elementValueType,
      numericValue: (valueType === 'number' || valueType === 'percentage') 
        ? newNumericValue 
        : (valueType === 'boolean' ? (newBooleanValue ? 1 : 0) : undefined),
      rangeMin: valueType === 'range' ? newRangeMin : undefined,
      rangeMax: valueType === 'range' ? newRangeMax : undefined,
      increment: valueType === 'range' ? newIncrement : undefined,
      linkedVariableId: newLinkedVariableId,
      linkedElementId: newLinkedElementId,
    });

    // Reset form fields
    setNewValue("");
    setNewDescription("");
    setNewNumericValue(undefined);
    setNewBooleanValue(false);
    setNewRangeMin(undefined);
    setNewRangeMax(undefined);
    setNewIncrement(undefined);
    setNewLinkedVariableId(undefined);
    setNewLinkedElementId(undefined);
    
    if (keepOpen) {
      // Keep row open and refocus the input for continuous entry
      setTimeout(() => newValueInputRef.current?.focus(), 0);
    } else {
      setIsAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newValue.trim()) {
      // Enter: add element and keep row open for adding more
      handleAddElement(true);
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setNewValue("");
      setNewDescription("");
    }
  };

  // Auto-add element when user clicks away from the add row
  const handleAddRowBlur = (e: React.FocusEvent) => {
    // Check if the new focus target is still within the add row
    const addRow = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node | null;
    
    // If relatedTarget is within the add row, user is just moving between inputs - don't close
    if (relatedTarget && addRow.contains(relatedTarget)) {
      return;
    }
    
    // Focus moved outside (or to non-focusable element where relatedTarget is null)
    // Add the value if present and close the row
    if (newValue.trim()) {
      handleAddElement(false); // Add and close
    } else {
      // No value entered, just close the add row
      setIsAdding(false);
    }
  };

  const getColumns = () => {
    const columns: { key: string; label: string; width: string }[] = [
      { key: 'value', label: 'Value', width: 'flex-1 min-w-[120px]' },
    ];

    if (valueType === 'type') {
      columns.push(
        { key: 'linked', label: 'Linked Value', width: 'flex-1 min-w-[120px]' },
        { key: 'description', label: 'Description', width: 'flex-1 min-w-[120px]' },
        { key: 'actions', label: '', width: 'w-12 shrink-0' }
      );
      return columns;
    }

    if (valueType === 'number') {
      columns.push({ key: 'number', label: 'Number', width: 'flex-1 min-w-[80px]' });
    } else if (valueType === 'percentage') {
      columns.push({ key: 'percentage', label: 'Percentage', width: 'flex-1 min-w-[80px]' });
    } else if (valueType === 'boolean') {
      columns.push({ key: 'boolean', label: 'Boolean', width: 'flex-1 min-w-[80px]' });
    } else if (valueType === 'range') {
      columns.push(
        { key: 'min', label: 'Min', width: 'flex-1 min-w-[60px]' },
        { key: 'max', label: 'Max', width: 'flex-1 min-w-[60px]' },
        { key: 'increment', label: 'Step', width: 'flex-1 min-w-[60px]' }
      );
    }

    columns.push(
      { key: 'linked', label: 'Linked Value', width: 'flex-1 min-w-[120px]' },
      { key: 'description', label: 'Description', width: 'flex-1 min-w-[120px]' },
      { key: 'actions', label: '', width: 'w-12 shrink-0' }
    );

    return columns;
  };

  const columns = getColumns();

  return (
    
    <div className="w-full">
      {/* Unified Header - full width with border */}
      <div className="w-full bg-surface border-b border-gray-200">
        <div className="w-full max-w-7xl mx-auto px-6 pt-6">
          <UnifiedWizardHeader
            title={editedName}
            mode={mode}
            itemId={variable.id}
            itemType="variable"
            onTitleChange={handleTitleChange}
            onCancel={handleCancel}
            onSubmit={handleSave}
            onEdit={isNewVariable ? undefined : handleEdit}
            onDelete={isNewVariable ? undefined : onVariableDelete}
            isSubmitting={false}
            submitDisabled={!editedName.trim()}
            submitText={isNewVariable ? "Create Variable" : "Save Variable"}
            hasUnsavedChanges={mode === 'edit'}
          />
        </div>
      </div>

      <div className="p-6 space-y-4 w-full max-w-7xl mx-auto">
      {/* Value Type Selector */}
      <div className="flex items-center gap-2 pb-4 border-b">
        <label className="text-sm font-medium text-muted-foreground">Value Type:</label>
        <select
          value={variable.element_type}
          onChange={(e) => onVariableUpdate({ element_type: e.target.value })}
          disabled={mode === 'view'}
          className="h-9 px-3 border rounded-md bg-background text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {VALUE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="border rounded-lg overflow-hidden overflow-x-auto">
        <div className="min-w-max">
          <div className="flex items-center bg-muted px-3 py-2 text-sm font-medium border-b">
            {columns.map((col) => (
              <div key={col.key} className={cn("px-2", col.width)}>
                {col.label}
              </div>
            ))}
          </div>

          <div className="divide-y">
          {variable.data?.map((element) => (
            <InlineElementRow
              key={element.id}
              element={element}
              valueType={valueType}
              allVariables={allVariables}
              currentVariableId={variable.id}
              onUpdate={onElementUpdate}
              onDelete={() => onElementDelete(element.id)}
              disabled={mode === 'view'}
            />
          ))}

          {isAdding && mode === 'edit' && (
            <div className="flex items-center px-3 py-2 bg-muted/30" onBlur={handleAddRowBlur}>
              <div className="flex-1 min-w-[120px] px-2">
                <Input
                  ref={newValueInputRef}
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter value..."
                  className="h-8"
                  autoFocus
                />
              </div>

              {valueType === 'number' && (
                <div className="flex-1 min-w-[80px] px-2">
                  <Input
                    type="number"
                    value={newNumericValue ?? ""}
                    onChange={(e) => setNewNumericValue(e.target.value ? Number(e.target.value) : undefined)}
                    onKeyDown={handleKeyDown}
                    placeholder="0"
                    className="h-8"
                  />
                </div>
              )}

              {valueType === 'percentage' && (
                <div className="flex-1 min-w-[80px] px-2">
                  <Input
                    type="number"
                    value={newNumericValue ?? ""}
                    onChange={(e) => setNewNumericValue(e.target.value ? Number(e.target.value) : undefined)}
                    onKeyDown={handleKeyDown}
                    placeholder="0%"
                    className="h-8"
                  />
                </div>
              )}

              {valueType === 'boolean' && (
                <div className="flex-1 min-w-[80px] px-2">
                  <select
                    value={newBooleanValue ? 'true' : 'false'}
                    onChange={(e) => setNewBooleanValue(e.target.value === 'true')}
                    className="h-8 w-full px-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="false">FALSE</option>
                    <option value="true">TRUE</option>
                  </select>
                </div>
              )}

              {valueType === 'range' && (
                <>
                  <div className="flex-1 min-w-[60px] px-2">
                    <Input
                      type="number"
                      value={newRangeMin ?? ""}
                      onChange={(e) => setNewRangeMin(e.target.value ? Number(e.target.value) : undefined)}
                      onKeyDown={handleKeyDown}
                      placeholder="Min"
                      className="h-8"
                    />
                  </div>
                  <div className="flex-1 min-w-[60px] px-2">
                    <Input
                      type="number"
                      value={newRangeMax ?? ""}
                      onChange={(e) => setNewRangeMax(e.target.value ? Number(e.target.value) : undefined)}
                      onKeyDown={handleKeyDown}
                      placeholder="Max"
                      className="h-8"
                    />
                  </div>
                  <div className="flex-1 min-w-[60px] px-2">
                    <Input
                      type="number"
                      value={newIncrement ?? ""}
                      onChange={(e) => setNewIncrement(e.target.value ? Number(e.target.value) : undefined)}
                      onKeyDown={handleKeyDown}
                      placeholder="Step"
                      className="h-8"
                    />
                  </div>
                </>
              )}

              <div className="flex-1 min-w-[120px] px-2">
                <LinkedValueSelector
                  variables={allVariables}
                  currentVariableId={variable.id}
                  linkedVariableId={newLinkedVariableId}
                  linkedElementId={newLinkedElementId}
                  onSelect={(varId, elemId) => {
                    setNewLinkedVariableId(varId);
                    setNewLinkedElementId(elemId);
                  }}
                />
              </div>

              <div className="flex-1 min-w-[120px] px-2">
                <Input
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Description (optional)"
                  className="h-8"
                />
              </div>

              {/* Empty spacer to maintain column alignment */}
              <div className="w-12 shrink-0 px-2" />
            </div>
          )}

          {(!variable.data || variable.data.length === 0) && !isAdding && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No values yet. Click the + button below to add your first value.</p>
            </div>
          )}
        </div>
        </div>
      </div>

      {mode === 'edit' && (
        <div className="flex justify-start">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setIsAdding(true);
              setTimeout(() => newValueInputRef.current?.focus(), 0);
            }}
            disabled={isAdding}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Value
          </Button>
        </div>
      )}

      <div className="space-y-2 pt-4 border-t">
        <label className="text-sm font-medium">Variable Description</label>
        <Textarea
          value={variableDescription}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          onBlur={handleDescriptionBlur}
          disabled={mode === 'view'}
          placeholder="Add an overall description for this variable..."
          rows={3}
          className="resize-none disabled:opacity-100 disabled:cursor-default"
        />
        {descriptionDirty && mode === 'edit' && (
          <p className="text-xs text-muted-foreground">Saving...</p>
        )}
      </div>

      {!isNewVariable && <VariableUsagePanel variable={variable} gameId={gameId} />}
      </div>
    </div>
  );
};
